import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { PhoneMask } from 'src/app/order/masks/phone-mask';
import { Messages } from 'src/app/order/messages/order.messages';
import { ProposalService } from 'src/app/order/services/external/proposal/proposal.service';
import { DialogService } from 'src/app/order/services/internal/dialog/dialog.service';
import { LoadingService } from 'src/app/order/services/internal/loading/loading.service';
import { RoutingService } from 'src/app/order/services/internal/routing/routing.service';
import { ScrollService } from 'src/app/order/services/internal/scroll/scroll.service';
import { WizardService } from 'src/app/order/services/internal/wizard/wizard.service';
import { DataStoreService } from 'src/app/order/store/data-store.service';
import { DataStore } from 'src/app/order/store/data.store';
import { EmailRegExp } from 'src/app/order/validators/email-compare-validator';
import { PhoneValidator } from 'src/app/order/validators/phone-validator';
import { Item } from 'src/app/shared/fd-form-components/fd-select/fd-select.component';
import { Proposal } from 'src/app/shared/models/proposal';
import { FdFieldConfigs } from 'src/app/shared/shared-components.module';
import { ConfigurationService } from 'src/app/start/services/configuration.service';
import { FormStep } from '../../../form-step';
import { TefComplementaryInfModel } from '../models/tef-complementary-inf.model';

@Component({
    selector: 'app-tef-complementary-inf',
    templateUrl: './tef-complementary-inf.component.html',
    styleUrls: ['./tef-complementary-inf.component.scss'],
    standalone: false
})
export class TefComplementaryInfComponent extends FormStep implements OnInit {
  public STEP_NUMBER = 0;

  public tefProviderTypes: Item[] = [
    {
      label: 'SiTef (SoftwareExpress)',
      value: TefProviderEnum.SITEF,
    },
    {
      label: 'SCOPE (Itautec/OKI Brasil)',
      value: TefProviderEnum.SCOPE,
    },
    {
      label: 'Direção (Linx)',
      value: TefProviderEnum.DIRECTION,
    },
    {
      label: 'CTF (auttar)',
      value: TefProviderEnum.CTF,
    },
    {
      label: 'Pay&Go (NTK)',
      value: TefProviderEnum.PAY_AND_GO,
    },
    {
      label: 'VBI',
      value: TefProviderEnum.VBI,
    },
  ];

  public tefComplementaryInfModel: TefComplementaryInfModel;

  constructor(
    private formBuilder: UntypedFormBuilder,
    protected proposalService: ProposalService,
    protected loadingService: LoadingService,
    protected dialogService: DialogService,
    protected dataStore: DataStore,
    protected scrollService: ScrollService,
    protected wizardService: WizardService,
    protected matDialog: MatDialog,
    protected configurationService: ConfigurationService,
    protected routingService: RoutingService,
    protected dataStoreService: DataStoreService
  ) {
    super(
      dataStore,
      scrollService,
      wizardService,
      matDialog,
      proposalService,
      loadingService,
      dialogService,
      configurationService,
      routingService,
      dataStoreService
    );
  }

  public async ngOnInit() {
    await super.ngOnInit();
  }

  public onProviderTypeChange(event: TefProviderEnum) {
    this.formControls.tefProviderTypes.setValue([event]);
  }

  public next(): void {
    this.submitted = true;

    if (!this.formControls.tefProviderTypes.valid) {
      this.dialogService.openDialog(Messages.MISSING_TEF_PROVIDER_TYPE);
      return;
    }

    if (this.isFormGroupValid()) {
      this.dataStore.setStepStatus(this.STEP_NUMBER, this.formGroup.valid);
      this.persistData().then((persisted) => {
        if (persisted) {
          this.wizardService.nextStep();
        }
      });
    }
  }

  public persistData() {
    const tefComplementaryInf: TefComplementaryInfModel = {
      ...this.formGroup.getRawValue(),
    };

    return this.dataStore.updateProposal({ tefComplementaryInf });
  }

  public createFormGroup() {
    return this.formBuilder.group({
      contactName: ['', Validators.required],
      contactEmail: ['', [Validators.required, Validators.pattern(EmailRegExp)]],
      contactMobileNumber: ['', [Validators.required, PhoneValidator]],
      integratorEnterpriseName: ['', Validators.required],
      contactPhoneNumber: ['', [Validators.required, PhoneValidator]],
      itResponsibleName: ['', Validators.required],
      itResponsiblePhoneNumber: ['', [Validators.required, PhoneValidator]],
      itResponsibleEmail: ['', [Validators.required, Validators.pattern(EmailRegExp)]],
      itResponsibleEmail2: ['', [Validators.required, Validators.pattern(EmailRegExp)]],
      tefProviderTypes: ['', Validators.required],
    });
  }
  protected proposalGetCallback(): void {}

  protected updateFields(proposal: Proposal) {
    this.tefComplementaryInfModel = proposal.tefComplementaryInf;

    if (this.tefComplementaryInfModel) {
      this.formControls.contactName.setValue(this.tefComplementaryInfModel.contactName);
      this.formControls.contactEmail.setValue(this.tefComplementaryInfModel.contactEmail);
      this.formControls.contactMobileNumber.setValue(this.tefComplementaryInfModel.contactMobileNumber);
      this.formControls.contactPhoneNumber.setValue(this.tefComplementaryInfModel.contactPhoneNumber);
      this.formControls.integratorEnterpriseName.setValue(this.tefComplementaryInfModel.integratorEnterpriseName);
      this.formControls.itResponsibleEmail.setValue(this.tefComplementaryInfModel.itResponsibleEmail);
      this.formControls.itResponsibleEmail2.setValue(this.tefComplementaryInfModel.itResponsibleEmail2);
      this.formControls.itResponsibleName.setValue(this.tefComplementaryInfModel.itResponsibleName);
      this.formControls.itResponsiblePhoneNumber.setValue(this.tefComplementaryInfModel.itResponsiblePhoneNumber);
      this.tefProviderTypes.map((x) => (x.selected = this.tefComplementaryInfModel.tefProviderTypes.indexOf(x.value) > -1));
      this.formControls.tefProviderTypes.setValue(this.tefComplementaryInfModel.tefProviderTypes);
    }
  }

  protected createFields(): FdFieldConfigs {
    return {
      contactName: {
        label: 'Nome',
        controlName: 'contactName',
        messages: {
          required: 'Informe o nome do contato',
        },
      },

      contactEmail: {
        label: 'E-mail',
        controlName: 'contactEmail',
        messages: {
          required: 'Informe o email do contato',
        },
      },

      contactMobileNumber: {
        label: 'Celular',
        mask: PhoneMask,
        maskCharsReplace: /[-( )]/g,
        controlName: 'contactMobileNumber',
        messages: {
          required: 'Informe o celular',
          invalid: 'Número inválido',
          pattern: 'Número inválido',
        },
      },
      integratorEnterpriseName: {
        label: 'Nome da empresa integradora',
        controlName: 'integratorEnterpriseName',
        messages: {
          required: 'Campo obrigatório',
        },
      },
      contactPhoneNumber: {
        label: 'Telefone de contato',
        mask: PhoneMask,
        maskCharsReplace: /[-( )]/g,
        controlName: 'contactPhoneNumber',
        messages: {
          required: 'Campo obrigatório',
          invalid: 'Número inválido',
          pattern: 'Número inválido',
        },
      },
      itResponsibleName: {
        label: 'Nome do responsável',
        controlName: 'itResponsibleName',
        messages: {
          required: 'Campo obrigatório',
        },
      },
      itResponsiblePhoneNumber: {
        label: 'Celular',
        mask: PhoneMask,
        maskCharsReplace: /[-( )]/g,
        controlName: 'itResponsiblePhoneNumber',
        messages: {
          required: 'Campo obrigatório',
          invalid: 'Número inválido',
          pattern: 'Número inválido',
        },
      },
      itResponsibleEmail: {
        label: 'E-mail',
        controlName: 'itResponsibleEmail',
        messages: {
          required: 'Campo obrigatório',
        },
      },
      itResponsibleEmail2: {
        label: 'E-mail 2',
        controlName: 'itResponsibleEmail2',
        messages: {
          required: 'Campo obrigatório',
        },
      },
      tefProviderTypes: {
        items: this.tefProviderTypes,
        controlName: 'tefProviderType',
        messages: {
          required: 'Informe o tipo de provedor TEF',
          invalid: 'Tipo provedor TEF inválido',
        },
      },
    };
  }
}
export enum ConnectionTypeEnum {
  TEF_IP = 'TEF_IP',
  TEF_X25 = 'TEF_X25',
}

export enum TefProviderEnum {
  SITEF = 'SITEF',
  SCOPE = 'SCOPE',
  DIRECTION = 'DIRECTION',
  CTF = 'CTF',
  PAY_AND_GO = 'PAY_AND_GO',
  VBI = 'VBI',
}
